import React, { useEffect, useMemo } from 'react'
import PropTypes from 'prop-types'
import { useAppContext } from '../../../redux/slices/appContext'
import { useListClientStatusReports } from '../../../api/groups'
import { useFormattingContext } from '../FormattingProvider/FormattingContext'
import SydSelect from '../../commonDesign/SydSelect'
import SydLabel from '../../commonDesign/SydLabel'
import { useStatusReportContext } from './statusReportContext'

function StatusReportVersionPicker ({
  label,
  description,
  placeholder,
  size,
  onChange,
  hideIfNoVersions
}) {
  const { clientId } = useAppContext()
  const { templateId, templatePurposeKey, selectedStatusReportId, setSelectedStatusReportId, report } = useStatusReportContext()
  const { formatter } = useFormattingContext()

  const { data, isLoading } = useListClientStatusReports({
    filters: {
      levelId: clientId,
      ...(templateId && { statusTemplateId: templateId }),
      ...(templatePurposeKey && { templatePurposeKey })
    }
  })

  const options = useMemo(() => {
    return data?.statusReports?.map(report => ({
      label: report.isActiveStatusReport ? 'Active Report ' : formatter(report.asOfDate, '@MMM D, YYYY'),
      value: report.statusReportId,
      key: report.statusReportId,
      description: report.isActiveStatusReport
        ? formatter(report.asOfDate, '@MMM D, YYYY')
        : (report.status === 'archived' ? 'Archived' : report.status === 'finalized' ? 'Finalized' : report.status)
    })) ?? []
  }, [data, formatter])

  // Initialize with current report if available and no selection has been made
  useEffect(() => {
    if (report?.statusReportId && selectedStatusReportId === null && !isLoading) {
      const currentReportInVersions = data?.statusReports?.find(v => v.statusReportId === report.statusReportId)
      if (currentReportInVersions) {
        setSelectedStatusReportId(report.statusReportId)
      }
    }
  }, [report, selectedStatusReportId, data, isLoading, setSelectedStatusReportId])

  // Determine the display value for the select
  const selectValue = useMemo(() => {
    // If we have an explicitly selected report ID, use it
    if (selectedStatusReportId !== null) {
      return selectedStatusReportId
    }

    // Otherwise, use the current report ID if available
    return report?.statusReportId || null
  }, [selectedStatusReportId, report])

  // Hide if no previous versions and hideIfNoVersions is true
  if (hideIfNoVersions && data?.statusReports?.length <= 1) {
    return null
  }

  const handleChange = (e) => {
    const value = e.target.value
    const statusReportId = value === 'null' || value === null ? null : value
    const selectedReport = statusReportId ? data?.statusReports?.find(v => v.statusReportId === statusReportId) : null

    // Update context
    setSelectedStatusReportId(statusReportId)

    // Call custom onChange if provided
    onChange?.(statusReportId, selectedReport)
  }

  return (
    <SydLabel
      label={label}
      description={description}
    >
      <SydSelect
        placeholder={isLoading ? 'Loading versions...' : placeholder}
        options={options}
        size={size}
        value={selectValue}
        onChange={handleChange}
        disabled={isLoading || !options.length}
      />
    </SydLabel>
  )
}

StatusReportVersionPicker.propTypes = {
  label: PropTypes.string,
  description: PropTypes.string,
  placeholder: PropTypes.string,
  size: PropTypes.oneOf(['xs', 'sm', 'lg']),
  onChange: PropTypes.func,
  hideIfNoVersions: PropTypes.bool
}

StatusReportVersionPicker.defaultProps = {
  label: 'Previous Versions',
  description: 'Select a previous version of this status report',
  placeholder: 'Select a version...',
  size: 'sm',
  hideIfNoVersions: true
}

export default StatusReportVersionPicker
